import React, { useEffect, useState } from "react"
import { api } from "../../lib/api"
import { useMe } from "../../hooks/useMe"
import { AppShell } from "../ui/AppShell"
import { Button } from "../ui/Button"
import { Card } from "../ui/Card"
import { PageLoading, Spinner } from "../ui/Spinner"
import { ProvaCard } from "./ProvaCard"
import { ComingSoonProvas } from "./ComingSoonProvas"
import Insights from "./Insights"
import { ConfirmEmailBanner } from "./ConfirmEmailBanner"
import { ActiveAttemptBanner } from "./ActiveAttemptBanner"
import { AccessCard } from "./AccessCard"
import type { ActiveAttempt, ProvaListItem, ProvasResponse } from "../../types"

/** Delay do reveal escalonado como CSS var (utilitário .reveal-up). */
function delay(ms: number): React.CSSProperties {
  return { "--reveal-delay": `${ms}ms` } as React.CSSProperties
}

export default function Dashboard() {
  const { me, loading, error, refetch } = useMe()
  const [provas, setProvas] = useState<ProvaListItem[] | null>(null)
  const [activeAttempt, setActiveAttempt] = useState<ActiveAttempt | null>(null)

  useEffect(() => {
    api
      .get<ProvasResponse>("/api/provas")
      .then((data) => {
        setProvas(data.provas)
        setActiveAttempt(data.active_attempt)
      })
      .catch(() => setProvas([]))
  }, [])

  if (loading) {
    return (
      <AppShell>
        <PageLoading />
      </AppShell>
    )
  }

  if (error || !me) {
    return (
      <AppShell>
        <div className="flex min-h-[50vh] flex-col items-center justify-center gap-4 text-center">
          <p className="text-navy-700">{error ?? "Erro ao carregar seus dados"}</p>
          <Button variant="secondary" onClick={refetch}>
            Tentar de novo
          </Button>
        </div>
      </AppShell>
    )
  }

  return (
    <AppShell>
      <div className="flex flex-col gap-6">
        {!me.confirmed && <ConfirmEmailBanner />}

        {/* Simulado em andamento — retomada sempre visível (decisão 3.4) */}
        {activeAttempt && <ActiveAttemptBanner attempt={activeAttempt} />}

        {/* Plano de acesso — ativo até X, ou o que ainda é grátis */}
        <AccessCard access={me.access} nivelamentoAvailable={me.nivelamento_available} oferta={me.oferta} />

        {/* Provas — o foco da tela */}
        <section aria-label="Suas provas" className="reveal-up" style={delay(0)}>
          <h2 className="mb-2.5 text-sm font-semibold uppercase tracking-wider text-navy-500">Suas provas</h2>
          {provas === null ? (
            <Card className="flex justify-center py-8">
              <Spinner />
            </Card>
          ) : provas.length === 0 ? (
            <Card className="py-8 text-center">
              <p className="text-sm text-navy-500">Nenhuma prova disponível no momento.</p>
            </Card>
          ) : (
            <div className={`grid grid-cols-1 gap-4 ${provas.length > 1 ? "md:grid-cols-2" : ""}`}>
              {provas.map((p) => (
                <ProvaCard key={p.id} prova={p} />
              ))}
            </div>
          )}

          {/* Próximas provas — sinaliza que o plano não fica preso à ESA */}
          {provas !== null && <ComingSoonProvas />}
        </section>

        {/* Boletim de desempenho — progresso (motivar) + onde melhorar (direcionar) */}
        <div className="reveal-up flex flex-col gap-6" style={delay(140)}>
          <Insights />
        </div>
      </div>
    </AppShell>
  )
}
